import type { SketchEntityReferenceSelectionEntrySnapshot } from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceSelectionIntentCollection';
import type {
    SketchEntityReferenceEffectiveSourceEntry,
    SketchEntityReferenceEffectiveSourcePlan,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceSelectionSourceCompiler';
import {
    resolveExternalGeometrySourceDescriptorKey,
    resolveTopologyFaceReferenceSourceKey,
	resolveTopologySourceMemberScopeKey,
} from '../../model/ExternalGeometrySourceDescriptor';
import {TopologyFaceRegionBoundaryResolver} from '../../source/topology/TopologyFaceRegionBoundaryResolver';
import {
    createSelectionIntentRecordSnapshot,
    type SketchEntityReferenceSelectionIntent,
} from './SketchEntityReferenceSelectionIntent';

/**
 * 转换实体引用有效来源编译器。
 *
 * 设计说明：
 * 1. PropertyManager 选择意图允许共享来源，执行层仍必须输出唯一来源；
 * 2. 多个面共同贡献的拓扑边也参与生成，但按稳定来源键只生成一次；
 * 3. 来源顺序和规范 Record 意图在同一次编译中确定，Preview/Create 不再各自推断。
 */
export class SketchEntityReferenceSelectionSourceCompiler {
	/** 创建与重建共用的相邻面区域外边界算法。 */
	private readonly faceRegionBoundaryResolver = new TopologyFaceRegionBoundaryResolver();

    /**
     * 编译当前有序选择意图。
     *
     * @param selections PropertyManager 有序选择快照。
     * @returns 唯一有效来源计划。
     */
    public compile(
        selections: readonly SketchEntityReferenceSelectionEntrySnapshot[],
    ): SketchEntityReferenceEffectiveSourcePlan {
        const faceKeysWithSelectedLoops = new Set<string>();
        selections.forEach((selection) => {
            const faceSource = selection.intent.topologyFaceSource;
            if (selection.intent.intentKind === 'topology-loop' && faceSource) {
                faceKeysWithSelectedLoops.add(
                    resolveTopologyFaceReferenceSourceKey(faceSource),
                );
            }
        });

        // 面继续保留为 PropertyManager 组根，但只要存在所属内环，就不再贡献 outer Wire。
        const loopFilteredSelections = selections
            .map((selection, selectionIndex) => ({ selection, selectionIndex }))
            .filter(({ selection }) => {
                const faceSource = selection.intent.topologyFaceSource;
                if (selection.intent.intentKind !== 'topology-face' || !faceSource) {
                    return true;
                }
                return !faceKeysWithSelectedLoops.has(
                    resolveTopologyFaceReferenceSourceKey(faceSource),
                );
            });
		const topologyFaceSelections = loopFilteredSelections.filter(({selection}) => (
			selection.intent.intentKind === 'topology-face'
			&& Boolean(selection.intent.topologyFaceSource)
		));
		const faceRegionResult = this.faceRegionBoundaryResolver.resolve({
			faces: topologyFaceSelections.map(selectionEntry => ({
				face: selectionEntry,
				ownerKey: resolveTopologySourceMemberScopeKey(
					selectionEntry.selection.intent.topologyFaceSource!.owner,
				),
				edges: selectionEntry.selection.sources.map(source => ({
					key: resolveExternalGeometrySourceDescriptorKey(source.descriptor),
					edge: source,
				})),
			})),
		});
		const regionSelections = faceRegionResult.regions.flatMap((region) => {
			const regionFaceSources = region.faces.map(face => (
				structuredClone(face.selection.intent.topologyFaceSource!)
			));
			const regionIntentId = region.faces.length === 1
				? region.faces[0]!.selection.intent.intentId
				: `topology-face-region::${regionFaceSources
					.map(source => resolveTopologyFaceReferenceSourceKey(source))
					.sort()
					.join('||')}`;
			return region.boundaryEdges.map((boundaryMember) => ({
				selectionIndex: boundaryMember.face.selectionIndex,
				selection: {
					...boundaryMember.face.selection,
					intent: {
						...boundaryMember.face.selection.intent,
						intentId: regionIntentId,
						topologyFaceRegionSources: regionFaceSources,
					},
					sources: [boundaryMember.edge],
				},
			}));
		});
		const effectiveSelections = [
			...loopFilteredSelections.filter(({selection}) => (
				selection.intent.intentKind !== 'topology-face'
			)),
			...regionSelections,
		].sort((left, right) => left.selectionIndex - right.selectionIndex);

        // 先按选择顺序和来源顺序展开，后续过滤不得改变剩余贡献的相对顺序。
        const sourceContributions = effectiveSelections.flatMap(({
            selection,
            selectionIndex,
        }) => (
            selection.sources.map((source) => ({
                selection,
                selectionIndex,
                source,
                sourceKey: resolveExternalGeometrySourceDescriptorKey(source.descriptor),
            }))
        ));

        const uniqueEntriesBySourceKey =
            new Map<string, SketchEntityReferenceEffectiveSourceEntry>();
        sourceContributions.forEach((contribution) => {
            const canonicalIntent = this.resolveCanonicalIntent(
                contribution.selection.intent,
                contribution.selectionIndex,
            );
            const existingEntry = uniqueEntriesBySourceKey.get(contribution.sourceKey);
            if (!existingEntry) {
                uniqueEntriesBySourceKey.set(contribution.sourceKey, {
                    sourceKey: contribution.sourceKey,
                    source: contribution.source,
                    canonicalIntent,
                    canonicalSelectionIndex: contribution.selectionIndex,
                });
                return;
            }

            // 复合意图比单边更能表达用户原始操作，但不得移动来源首次有效出现的位置。
            if (
                existingEntry.canonicalIntent.intentKind === 'single-source'
                && canonicalIntent.intentKind !== 'single-source'
            ) {
                uniqueEntriesBySourceKey.set(contribution.sourceKey, {
                    ...existingEntry,
                    source: contribution.source,
                    canonicalIntent,
                    canonicalSelectionIndex: contribution.selectionIndex,
                });
            }
        });

        const entries = Array.from(uniqueEntriesBySourceKey.values());
        const sourceCountsByIntentId = entries.reduce<Map<string, number>>(
            (counts, entry) => {
                counts.set(
                    entry.canonicalIntent.intentId,
                    (counts.get(entry.canonicalIntent.intentId) ?? 0) + 1,
                );
                return counts;
            },
            new Map(),
        );
        const sourceIndicesByIntentId = new Map<string, number>();
        const sourceDisplayLabelsByKey: Record<string, string> = {};
        const selectionIntentsBySourceKey: Record<string, ReturnType<
            typeof createSelectionIntentRecordSnapshot
        >> = {};
        const runtimeTopologySourcesBySourceKey: Record<
            string,
            SketchEntityReferenceEffectiveSourcePlan[
                'runtimeTopologySourcesBySourceKey'
            ][string]
        > = {};

        entries.forEach((entry) => {
            const intentId = entry.canonicalIntent.intentId;
            const sourceIndex = sourceIndicesByIntentId.get(intentId) ?? 0;
            sourceIndicesByIntentId.set(intentId, sourceIndex + 1);
            sourceDisplayLabelsByKey[entry.sourceKey] =
                entry.canonicalIntent.intentKind === 'single-source'
                    ? entry.canonicalIntent.displayLabel
                    : entry.source.displayLabel;
            selectionIntentsBySourceKey[entry.sourceKey] =
                createSelectionIntentRecordSnapshot({
                    intent: entry.canonicalIntent,
                    sourceIndex,
                    sourceCount: sourceCountsByIntentId.get(intentId) ?? 0,
                });
            if (entry.source.runtimeTopologySource) {
                runtimeTopologySourcesBySourceKey[entry.sourceKey] =
                    entry.source.runtimeTopologySource;
            }
        });

        return {
            entries,
            sources: entries.map((entry) => entry.source.descriptor),
            sourceDisplayLabelsByKey,
            selectionIntentsBySourceKey,
            runtimeTopologySourcesBySourceKey,
        };
    }

    /**
     * 解析写入 Record 的规范意图展示标签。
     *
     * @param intent 原始选择意图。
     * @param selectionIndex PropertyManager 中的选择序号。
     * @returns 可稳定写入当前会话记录的规范意图。
     */
    private resolveCanonicalIntent(
        intent: SketchEntityReferenceSelectionIntent,
        selectionIndex: number,
    ): SketchEntityReferenceSelectionIntent {
        return {
            ...intent,
            displayLabel: intent.intentKind === 'single-source'
                ? `边线${selectionIndex + 1}`
                : intent.displayLabel,
        };
    }
}
